import {
	TicketDTO,
	TicketDivisionDTO,
	TicketLineDTO,
	ClosingQuantityDTO,
	InvoiceParamsFromPosDTO,
	SaleDTO,
	Res,
	InvoiceDTO
} from 'core/api';
import { Result, CustomError, handleServerErrors, ServerError, Events } from 'core/infrastructure';

import {
	InvoiceFacade, SplitTicketFacade
} from 'pos/facades';

let closingTicket: Nullable<TicketDTO> = null;
let originalTicket: Nullable<TicketDTO> = null;

export async function createDivision(invoice: InvoiceDTO): Promise<Result<TicketDivisionDTO, CustomError>> {

	const result = await safeSplit(async () => {
		const ticket = await InvoiceFacade.createTicketFromInvoice(invoice.Id);
		return SplitTicketFacade.createDivision(ticket);
	});

	if (result.ok) {
		originalTicket = result.value.Left;
	}

	return result;
}


export function moveOneUnitToRight(division: TicketDivisionDTO, line: TicketLineDTO): Promise<Result<TicketDivisionDTO>> {
	return safeSplit(() => {
		return SplitTicketFacade.moveOneUnitToRight(division, line);
	});
}

export function moveOneUnitToLeft(division: TicketDivisionDTO, line: TicketLineDTO): Promise<Result<TicketDivisionDTO>> {
	return safeSplit(() => {
		return SplitTicketFacade.moveOneUnitToLeft(division, line);
	});
}


export async function beginClosingTicket(closingQuantities: ClosingQuantityDTO[], closingTableCompanions: Nullable<number>): Promise<Result> {

	const result = await safeSplit(() => {
		return SplitTicketFacade.beginClosingTicket(getOriginalTicket().Id, closingQuantities, closingTableCompanions);
	});

	if (result.ok)
		closingTicket = result.value;

	return result;
}

export function cancelOriginalTicket() {

	return safeSplit(async () => {

		// Ignoramos los errores de TicketNotFound, TicketFrozen, EmptyTicket porque eso quiere decir que de alguna otra forma
		// antes de que cancelemos el ticket que estabamos usando como cuenta parcial, alguien lo ha borrado, cerrado,
		// etc. y no hace falta hacer nada porque ya no podemos hacer nada con ese ticket. En esos casos también establecemos a null
		// el closingTicket
		return await handleServerErrors(async () => {
			const result = await InvoiceFacade.deleteTicketFromInvoice(getOriginalTicket().Id);
			originalTicket = null;
			return result;
		}, {
			onTicketNotFoundError: async (_error) => { },
			onFrozenTicketError: async (_error) => { },
			onEmptyTicketError: async (_error) => { },
		});

	});
}

export async function cancelClosingTicket(): Promise<Result> {

	if (closingTicket === null)
		return Result.ok();

	return safeSplit(async () => {

		// Ignoramos los errores de TicketNotFound, TicketFrozen, EmptyTicket porque eso quiere decir que de alguna otra forma
		// antes de que cancelemos el ticket que estabamos usando como cuenta parcial, alguien lo ha borrado, cerrado,
		// etc. y no hace falta hacer nada porque ya no podemos hacer nada con ese ticket. En esos casos también establecemos a null
		// el closingTicket
		return await handleServerErrors(async () => {
			const result = await SplitTicketFacade.cancelClosingTicket(getClosingTicket().Id, getOriginalTicket().Id);
			closingTicket = null;
			return result;
		}, {
			onTicketNotFoundError: async (_error) => { },
			onFrozenTicketError: async (_error) => { },
			onEmptyTicketError: async (_error) => { },
		});

	});
}


export async function refundClosingTicket(params: InvoiceParamsFromPosDTO): Promise<Result<SaleDTO, CustomError>> {

	const result = await safeSplit(async () => {
		const result = await InvoiceFacade.createPartialInvoiceRefund(getClosingTicket(), params);
		await Events.raise('pos:invoice:changed');
		return result;
	});

	if (!result.ok)
		return result;

	await cancelOriginalTicket();
	return result;
}

export function getClosingTicket(): TicketDTO {

	if (closingTicket === null)
		throw new Error('No se ha iniciado ninguna operación con la cuenta parcial');

	return closingTicket;
}

export function getOriginalTicket(): TicketDTO {

	if (originalTicket === null)
		throw new Error('No se ha iniciado ninguna división');

	return originalTicket;
}

export async function safeSplit<T>(fn: () => Promise<T>): Promise<Result<T, CustomError>> {

	let handledError: CustomError = new ServerError('ERROR NO MANEJADO');

	const result = await handleServerErrors(fn, {

		onEmptyTicketError: async error => {
			handledError = error;
			await Events.raise('pos:partial-refund:operation-failed', { msg: Res.Msg.MSG_TICKET_HAS_BEEN_MODIFIED_AND_NOW_IT_IS_EMPTY(), error });
		},

		onPrintError: async error => {
			handledError = error;
			await Events.raise('pos:partial-refund:operation-failed', { msg: Res.Msg.MSG_TICKET_COULD_NOT_BE_PRINTED(error.message), error });
		},

		onInvalidPaymentError: async error => {
			handledError = error;
			await Events.raise('pos:partial-refund:operation-failed', { msg: error.message, error });
		},

		onDomainError: async error => {
			handledError = error;
			await Events.raise('pos:partial-refund:operation-failed', { msg: error.message, error });
		},

		onTicketNotFoundError: async error => {
			handledError = error;
			await Events.raise('pos:partial-refund:operation-failed', { msg: Res.Msg.MSG_TICKET_HAS_BEEN_DELETED_OR_CLOSED_BY_ANOTHER_USER(), error });
		},

		onFrozenTicketError: async error => {
			handledError = error;
			await Events.raise('pos:partial-refund:operation-failed', { msg: Res.Msg.MSG_CANNOT_MODIFY_FROZEN_TICKET(), error });
		},

		onTicketSynchronizationError: async error => {
			handledError = error;

			await Events.raise('pos:partial-refund:operation-failed', { msg: Res.Msg.MSG_TICKET_HAS_BEEN_MODIFIED_SO_CHECK_THE_NEW_COMPOSITION(), error });
		}
	});

	return result.ok ? Result.ok(result.value) : Result.error(handledError);
}

